import React, { useState } from 'react';
import { useBooking, Service } from '../../contexts/booking/BookingContext';
import { supabase } from '../../lib/supabaseClient';
import { format, parseISO, addMinutes } from 'date-fns';
import { 
  getNumericPrice, 
  getNumericDuration, 
  getTotalDuration, 
  getTotalPrice, 
  formatPrice 
} from '../../utils/bookingUtils';

/**
 * Component for reviewing and confirming booking details
 */
const BookingSummary: React.FC = () => {
  const { bookingState, prevStep, nextStep, setAppointmentId } = useBooking();
  const { selectedServices, selectedStaff, selectedDate, selectedTime, customerInfo } = bookingState;
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Validate required data
  if (!selectedDate || !selectedTime || !customerInfo) {
    return <div>Missing required booking information. Please go back and complete all steps.</div>;
  }
  
  // Format the appointment time for display
  if (!selectedTime) {
    console.error('[BookingSummary] selectedTime is null or undefined. Cannot proceed.');
    return <div>Error: Booking time not selected. Please go back.</div>;
  }
  
  const appointmentDateTime = parseISO(selectedTime);
  const totalDuration = getTotalDuration(selectedServices);
  const appointmentEndTime = addMinutes(appointmentDateTime, totalDuration);
  
  /**
   * Handles the booking submission to the database
   */
  const handleSubmitBooking = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Format dates for database
      const startTimeISO = appointmentDateTime.toISOString();
      const endTimeISO = appointmentEndTime.toISOString();
      
      // Get current authenticated user (if any)
      const { data: { user } } = await supabase.auth.getUser();
      
      // Get or create client ID
      const clientId = await getOrCreateClientId(user);
      
      // Create the appointment with pending_payment status
      const appointmentId = await createAppointment(clientId, startTimeISO, endTimeISO);
      
      // Add services to the appointment
      await addServicesToAppointment(appointmentId);
      
      // Store the appointment ID in the booking context
      setAppointmentId(appointmentId);
      
      // Move to the payment step
      nextStep();
      
    } catch (err: any) {
      console.error('Error creating booking:', err);
      setError(err.message || 'An error occurred while creating your booking.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  /**
   * Gets existing client ID or creates a new client profile
   */
  const getOrCreateClientId = async (user: any): Promise<string> => {
    // If user is authenticated, use their ID or create profile
    if (user) {
      const clientId = user.id;
      console.log('Using authenticated user ID:', clientId);
      
      // Check if user profile exists
      const { error: profileError } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('id', clientId)
        .single();
        
      // If profile doesn't exist, create it
      if (profileError) {
        console.log('Creating profile for authenticated user...');
        const { error: insertError } = await supabase
          .from('user_profiles')
          .insert({
            id: clientId,
            full_name: `${customerInfo!.firstName} ${customerInfo!.lastName}`,
            preferred_name: customerInfo!.firstName,
            email: customerInfo!.email,
            phone_number: customerInfo!.phone,
            account_status: 'active'
          });
          
        if (insertError) {
          throw new Error(`Error creating profile: ${insertError.message}`);
        }
      }
      
      return clientId;
    }
    
    // User not authenticated - check if email exists in profiles
    const { data: existingProfiles, error: searchError } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('email', customerInfo!.email);
      
    if (searchError) {
      throw new Error(`Error searching for profiles: ${searchError.message}`);
    }
    
    // If profile exists, use it
    if (existingProfiles && existingProfiles.length > 0) {
      const existingId = existingProfiles[0].id;
      console.log('Using existing profile ID:', existingId);
      return existingId;
    }
    
    // Create new profile with guest status
    const newUUID = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString();
    
    const { data: newProfile, error: createError } = await supabase
      .from('user_profiles')
      .insert({
        id: newUUID,
        full_name: `${customerInfo!.firstName} ${customerInfo!.lastName}`,
        preferred_name: customerInfo!.firstName,
        email: customerInfo!.email,
        phone_number: customerInfo!.phone,
        account_status: 'guest'
      })
      .select('id')
      .single();
      
    if (createError) {
      throw new Error(`Error creating new profile: ${createError.message}`);
    }
    
    if (!newProfile) {
      throw new Error('Failed to create new profile');
    }
    
    console.log('Created new profile with ID:', newProfile.id);
    return newProfile.id;
  };
  
  /**
   * Creates the main appointment record
   */
  const createAppointment = async (clientId: string, startTime: string, endTime: string): Promise<string> => {
    // Create the appointment with the required status field
    console.log('Creating appointment with client ID:', clientId);
    // Try with different status values that are likely allowed
    const statusOptions = ['scheduled', 'confirmed', 'booked', 'active'];
    let appointment = null;
    let appointmentError = null;
    
    // Try each status value until one works
    for (const statusValue of statusOptions) {
      console.log(`Trying to create appointment with status: ${statusValue}`);
      const result = await supabase
        .from('appointments')
        .insert({
          client_id: clientId,
          service_id: selectedServices[0].id,
          staff_id: selectedStaff?.id || null,
          start_time: startTime,
          end_time: endTime,
          status: statusValue,
          notes: customerInfo?.notes || null,
        })
        .select('id')
        .single();
      
      // If successful, use this result
      if (!result.error) {
        appointment = result.data;
        appointmentError = null;
        console.log(`Successfully created appointment with status: ${statusValue}`);
        break;
      } else {
        console.error(`Error with status '${statusValue}':`, result.error);
        appointmentError = result.error;
      }
    }
    
    if (appointmentError) {
      throw new Error(`Error creating appointment: ${appointmentError.message}`);
    }
    
    if (!appointment) {
      throw new Error('Failed to create appointment with any status value');
    }
    
    return appointment.id;
  };
  
  /**
   * Adds all selected services to the appointment_services junction table
   */
  const addServicesToAppointment = async (appointmentId: string): Promise<void> => {
    const appointmentServices = selectedServices.map(service => ({
      appointment_id: appointmentId,
      service_id: service.id,
      price: getNumericPrice(service),
      duration: getNumericDuration(service),
      staff_id: selectedStaff?.id || null,
    }));
    
    const { error: servicesError } = await supabase
      .from('appointment_services')
      .insert(appointmentServices);
    
    if (servicesError) {
      throw new Error(`Error adding services to appointment: ${servicesError.message}`);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-serif text-[#232347] mb-2">Review Your Booking</h2>
      <p className="text-gray-600 mb-6">Please review your appointment details before confirming</p>
      
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
          <h3 className="font-medium mb-1">Error</h3>
          <p>{error}</p>
        </div>
      )}
      
      <div className="bg-[#F1F7F4] p-6 rounded-md mb-6">
        <h3 className="text-xl font-serif text-[#232347] mb-4">Appointment Summary</h3>
        
        {/* Date & Time */}
        <div className="mb-4">
          <h4 className="font-medium text-[#232347] mb-2">Date & Time</h4>
          <div className="flex items-center space-x-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#B2D8C5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-gray-700">
              {format(appointmentDateTime, 'EEEE, MMMM d, yyyy')} at {format(appointmentDateTime, 'h:mm a')} - {format(appointmentEndTime, 'h:mm a')}
            </span>
          </div>
        </div>
        
        {/* Staff */}
        <div className="mb-4">
          <h4 className="font-medium text-[#232347] mb-2">Therapist</h4>
          <div className="flex items-center space-x-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#B2D8C5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="text-gray-700">
              {selectedStaff ? selectedStaff.name : 'No preference (best available therapist)'}
            </span>
          </div>
        </div>
        
        {/* Services */}
        <div className="mb-4">
          <h4 className="font-medium text-[#232347] mb-2">Services</h4>
          <div className="space-y-2">
            {selectedServices.map(service => (
              <div key={service.id} className="flex justify-between items-center text-gray-700 pl-7">
                <span>{service.name} ({getNumericDuration(service)} min)</span>
                <span>${formatPrice(getNumericPrice(service))}</span>
              </div>
            ))}
            <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between font-medium text-[#232347] pl-7">
              <span>Total</span>
              <span>${formatPrice(getTotalPrice(selectedServices))}</span>
            </div>
          </div>
        </div>
        
        {/* Customer Info */}
        <div>
          <h4 className="font-medium text-[#232347] mb-2">Your Information</h4>
          <div className="space-y-2 pl-7">
            <div className="text-gray-700">
              {customerInfo.firstName} {customerInfo.lastName}
            </div>
            <div className="text-gray-700">
              Email: {customerInfo.email}
            </div>
            <div className="text-gray-700">
              Phone: {customerInfo.phone}
            </div>
            {customerInfo.notes && (
              <div className="text-gray-700">
                <p className="font-medium">Notes:</p>
                <p>{customerInfo.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Cancellation Policy */}
      <div className="mb-6 bg-white border border-gray-200 p-4 rounded-md">
        <h3 className="font-medium text-[#232347] mb-2">Cancellation Policy</h3>
        <p className="text-gray-600 text-sm">
          Please provide at least 24 hours notice for cancellations or rescheduling to avoid a cancellation fee of 50% of the service price. 
          No-shows will be charged the full service price.
        </p>
      </div>
      
      {/* Navigation buttons */}
      <div className="mt-8 flex justify-between">
        <button
          onClick={prevStep}
          disabled={isSubmitting}
          className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>
        <button
          onClick={handleSubmitBooking}
          disabled={isSubmitting}
          className="px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {isSubmitting ? (
            <>
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
              Processing...
            </>
          ) : (
            'Confirm Booking'
          )}
        </button>
      </div>
    </div>
  );
};

export default BookingSummary;